import { Button } from "$components/button/button";
import { Autocomplete, DatePicker } from "$components/rhf-fields";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import { RHFLocationSelect } from "$routes/inventory/_lib/location-select";
import { RHFTextField } from "$routes/inventory/_lib/rhf-text-field";
import SkuSearchInput from "$routes/inventory/_lib/sku-search-input";
import { TextField } from "$routes/inventory/_lib/text-field";
import type { Supplier } from "$routes/inventory/_lib/types";
import { gql, useMutation, useQuery } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { Grid, makeStyles } from "@material-ui/core";
import dayjs from "dayjs";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useInventoryTableFilterContext } from "../inventory-context";
import { useResetTableCache } from "../queries";
import {
  type FormValues,
  useReceiveValidationSchema,
} from "./use-receive-validation-schema";

const SUPPLIER_LIST = gql`
  query supplierReadAll {
    supplierReadAll {
      id
      name
    }
  }
`;

const CREATE_LOT_MUTATION = gql`
  mutation createLot($input: ReceiveStockInput!) {
    receiveStock(input: $input) {
      expiryDate
      facilityId
      locationId
      id
      quantity
      skuId
    }
  }
`;

const useStyles = makeStyles((theme) => ({
  form: {
    marginTop: theme.spacing(2),
  },
  submitFooter: {
    display: "flex",
    justifyContent: "flex-end",
  },
  root: {
    "& $element": {
      color: theme.palette.primary.contrastText,
    },
  },
  link: {
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
    },
  },
}));

const allDefined = (items: unknown[]): boolean => items.every((item) => !!item);

export function ReceiveForm() {
  const { facilityId } = useUser();
  const inventoryTableFilterState = useInventoryTableFilterContext();
  const {
    setLoading,
    locationId: defaultLocationId,
    reset: resetInventoryOverviewState,
    defaultInventorySortBy,
  } = inventoryTableFilterState;

  const validationSchema = useReceiveValidationSchema();
  const formMethods = useForm<FormValues>({
    defaultValues: { locationId: defaultLocationId, expiryDate: null },
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
  });
  const { handleSubmit, watch, reset, setValue } = formMethods;

  const { formatMessage } = useIntl();

  const resetTableCache = useResetTableCache();
  const [submit, { loading: submittedLoading }] =
    useMutation(CREATE_LOT_MUTATION);

  const locationId = watch("locationId");
  const supplier = watch("supplier");
  const sku = watch("sku");

  const onSubmitReceive = async (form: FormValues) => {
    try {
      setLoading(true);
      await submit({
        variables: {
          input: {
            supplierId: form.supplier.id,
            skuId: form.sku.id,
            quantity: form.quantityPackSize * form.quantityPacks,
            expiryDate: dayjs(form.expiryDate)
              .endOf("month")
              .format("YYYY-MM-DD"),
            facilityId,
            locationId: form.locationId,
          },
        },
      });
      toast.success(
        formatMessage({
          id: "inventory.form.receive.submit.success",
          defaultMessage: "Item successfully added.",
        }),
      );
      reset({
        supplier: form.supplier,
        locationId: form.locationId,
      });
      // if the receive location is different from the current filter switch back to showing stock across the whole facility so the new lot shows
      const nextLocation =
        form.locationId === inventoryTableFilterState.locationId
          ? form.locationId
          : null;
      resetTableCache({
        skuIdToClearChildLotsFor: sku.id,
        facilityId,
        locationId: nextLocation,
      });
      resetInventoryOverviewState({
        searchTerm: "",
        sortBy: defaultInventorySortBy,
        locationId: nextLocation,
      });
    } catch (_e) {
      toast.error(
        formatMessage({
          id: "inventory.form.receive.submit.error",
          defaultMessage: "Something went wrong while submitting receive data.",
        }),
      );
    }
  };

  const quantityPacks = watch("quantityPacks") ?? 0;
  const quantitySize = watch("quantityPackSize") ?? 0;
  const total = quantityPacks * quantitySize;

  const { loading: suppliersLoading, data: suppliersData } =
    useQuery(SUPPLIER_LIST);

  useEffect(() => {
    if (suppliersData?.supplierReadAll?.length === 1) {
      setValue("supplier", suppliersData.supplierReadAll[0]);
    }
  }, [setValue, suppliersData]);

  const classes = useStyles();

  return (
    <form onSubmit={handleSubmit(onSubmitReceive)} className={classes.form}>
      <FormProvider {...formMethods}>
        <RHFLocationSelect
          id="receive-location-select"
          label={formatMessage({
            id: "inventory.form.receive.location.label",
            defaultMessage: "Location",
          })}
        />
        <Autocomplete
          id="supplier-search"
          name="supplier"
          data-testid="supplier-search"
          fullWidth
          loading={suppliersLoading}
          options={suppliersData?.supplierReadAll || []}
          getOptionLabel={(option: Supplier) => option.name}
          forcePopupIcon={false}
          freeSolo
          renderInput={(errStr) => (params) => (
            <TextField
              {...params}
              label={formatMessage({
                id: "inventory.form.receive.supplierSearch.label",
                defaultMessage: "Supplier",
              })}
              variant="outlined"
              InputProps={{
                ...params.InputProps,
                type: "search",
              }}
              error={errStr}
              required
            />
          )}
        />
        <SkuSearchInput
          name="sku"
          analyticsEventName="inventory_management.receiveForm.skuSearch"
          required
        />
        {allDefined([locationId, supplier, sku]) && (
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name="quantityPackSize"
                type="number"
                label={formatMessage({
                  id: "inventory.form.receive.quantityPackSize.label",
                  defaultMessage: "No of units in pack",
                })}
                data-testid="units-in-pack-field"
                variant="outlined"
                inputProps={{
                  min: 1,
                  max: 2147483647,
                }}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name="quantityPacks"
                type="number"
                label={formatMessage({
                  id: "inventory.form.receive.quantityPacks.label",
                  defaultMessage: "No of packs",
                })}
                data-testid="number-of-packs-field"
                variant="outlined"
                inputProps={{
                  min: 1,
                  max: 2147483647,
                }}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name="totalUnits"
                value={total}
                disabled
                label={formatMessage({
                  id: "inventory.form.receive.totalUnits.label",
                  defaultMessage: "Dispensible Units",
                })}
                data-testid="total-units-field"
                variant="outlined"
                inputProps={{
                  min: 0,
                  max: 2147483647,
                }}
                fullWidth
              />
            </Grid>
          </Grid>
        )}

        {allDefined([locationId, supplier, sku, total]) && (
          <DatePicker
            name="expiryDate"
            label={formatMessage({
              id: "inventory.form.receive.expiryDate.label",
              defaultMessage: "Expiry Date",
            })}
            views={["year", "month"]}
            fullWidth
            required
            disablePast
            format="MM/yyyy"
          />
        )}

        {allDefined([locationId, supplier, sku, total]) && (
          <div className={classes.submitFooter}>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              loading={submittedLoading}
              data-testid="receive-submit-button"
              disabled={!formMethods.formState.isValid}
            >
              {formatMessage({
                id: "inventory.form.receive.submitButton",
                defaultMessage: "Receive Item",
              })}
            </Button>
          </div>
        )}
      </FormProvider>
    </form>
  );
}
